import { IonFooter, IonHeader, IonIcon, IonTitle, IonToolbar, useIonRouter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/button";
import { Buttons } from "@/components/buttons";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { useBoomerForm } from "@/form/boomer/hook";
import { AppFormFieldInfo } from "@/form/components/field-info";
import { AppFormFieldLabel } from "@/form/components/field-label";
import { api } from "@/lib/backend";
import type { ExpenseClaim } from "@/lib/backend/api";
import { TRAVEL_EXPENSE_TYPE, type TravelExpenseType } from "@/lib/constants";
import { xMarkIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { carIcon } from "@/lib/icons/car.icon";
import { checkIcon } from "@/lib/icons/check.icon";
import { crossIcon } from "@/lib/icons/cross.icon";
import { dotsHorizontalIcon } from "@/lib/icons/dots-horizontal.icon";
import { fileIcon } from "@/lib/icons/file.icon";
import { trainIcon } from "@/lib/icons/train.icon";
import { queryClient } from "@/lib/providers/query";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";

const TOAST_KEY = "shift-page";

export function TravelExpensesSection({
  id,
  modal,
  shiftTimeEntryId,
  expenseClaim,
}: {
  id: string;
  travelExpensesDeclared: number;
  modal: string | undefined;
  shiftTimeEntryId: string | undefined;
  expenseClaim?: ExpenseClaim | null;
}) {
  const { t } = useTranslation();
  const router = useIonRouter();

  const travelExpensesMutation = useMutation({
    mutationKey: ["shift", id, "travel-expenses"],
    mutationFn: async (variables: {
      id: string;
      types: TravelExpenseType[];
      files: File[];
      cost: number | null;
      note: string;
    }) => {
      if (!shiftTimeEntryId) {
        throw new Error(t("errors:travel-expenses.no-shift-declaration"));
      }

      if (variables.files.length === 0) {
        throw new Error(t("errors:travel-expenses.no-file"));
      }

      if (variables.cost === null) {
        throw new Error(t("errors:travel-expenses.no-cost"));
      }

      if (variables.types.length === 0) {
        throw new Error(t("errors:travel-expenses.no-type"));
      }

      if (variables.files.length > 1) {
        throw new Error(t("errors:travel-expenses.too-many-files"));
      }

      await api.expenseClaim.createForShiftTimeEntry(shiftTimeEntryId, {
        type: variables.types[0].toUpperCase() as "CAR" | "PUBLIC_TRANSPORT" | "OTHER",
        amount: variables.cost,
        note: variables.note,
        document: variables.files[0],
        status: "PENDING",
      });
    },
    onMutate: () => {
      toast.loading(t("actions:declare-travel-expenses.loading"), { id: TOAST_KEY });
    },
    onSuccess: () => {
      toast.success(t("actions:declare-travel-expenses.success"), { id: TOAST_KEY });
      queryClient.invalidateQueries({ queryKey: ["shift"] });
    },
    onError: (error) => {
      toast.error(t("actions:declare-travel-expenses.error"), { id: TOAST_KEY });
      logger.error(error);
    },
  });

  const [formTravelExpenses, { Element: FormElement }] = useBoomerForm({
    defaultValues: {
      types: [],
      files: [],
      cost: null,
      note: "",
    } as {
      types: TravelExpenseType[];
      files: File[];
      cost: number | null;
      note: string;
    },
    onSubmit: async ({ value }) => {
      await travelExpensesMutation.mutateAsync({
        id,
        ...value,
      });
      router.push(Routes.Shift({ id }, { search: { modal: undefined } }), undefined, "replace");
    },
  });

  return (
    <>
      <div className="flex flex-col gap-3">
        <h2 className="text-xl font-bold">{t("routes:shift.travel-expenses")}</h2>

        <Button
          color="primary"
          fill="outline"
          onClick={() =>
            router.push(
              Routes.Shift({ id }, { search: { modal: "travel-expenses" } }),
              undefined,
              "replace",
            )
          }
          disabled={!!expenseClaim}
          className="aria-disabled:!opacity-100"
        >
          <div className="flex items-center gap-2">
            {expenseClaim
              ? t(`enums:travelExpenseEntryStatus.${expenseClaim.status}`)
              : t("modals:travel-expenses.trigger")}
            <IonIcon icon={expenseClaim ? checkIcon : fileIcon} className="size-4" />
          </div>
        </Button>
      </div>
      <ModalSheetAutoHeight
        isOpen={modal === "travel-expenses"}
        onWillDismiss={() =>
          router.push(Routes.Shift({ id }, { search: { modal: undefined } }), undefined, "replace")
        }
        onDidDismiss={() => formTravelExpenses.reset()}
      >
        <FormElement>
          <IonHeader>
            <IonToolbar>
              <Buttons slot="start">
                <Button
                  color="primary"
                  onClick={() =>
                    router.push(
                      Routes.Shift({ id }, { search: { modal: undefined } }),
                      undefined,
                      "replace",
                    )
                  }
                >
                  <IonIcon slot="icon-only" icon={crossIcon} className="size-6" />
                </Button>
              </Buttons>
              <IonTitle>{t("modals:travel-expenses.title")}</IonTitle>
            </IonToolbar>
          </IonHeader>
          <div className="ion-padding flex flex-col gap-8">
            <formTravelExpenses.Field
              name="types"
              validators={{
                onChange: z
                  .array(z.enum(TRAVEL_EXPENSE_TYPE))
                  .length(1, t("fields:travelExpenses.type.error.required")),
                onSubmit: z
                  .array(z.enum(TRAVEL_EXPENSE_TYPE))
                  .length(1, t("fields:travelExpenses.type.error.required")),
              }}
              children={(field) => (
                <div className="flex flex-col gap-2">
                  <AppFormFieldLabel field={field} name="travelExpenses.type" />

                  <div className="flex gap-3 *:flex-1">
                    {TRAVEL_EXPENSE_TYPE.map((type) => (
                      <Button
                        key={type}
                        data-active={field.state.value.includes(type)}
                        fill="outline"
                        className="ion-rounded-xl ion-border-2 part-native:border-transparent part-native:bg-tertiary data-[active=true]:part-native:border-brand-500"
                        onClick={() => field.handleChange([type])}
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <IonIcon
                            icon={
                              type === "CAR"
                                ? carIcon
                                : type === "PUBLIC_TRANSPORT"
                                  ? trainIcon
                                  : dotsHorizontalIcon
                            }
                            className="size-9"
                          />
                          <span className="font-medium">
                            {type === "CAR"
                              ? t("fields:travelExpenses.type.options.car")
                              : type === "PUBLIC_TRANSPORT"
                                ? t("fields:travelExpenses.type.options.public-transport")
                                : t("fields:travelExpenses.type.options.other")}
                          </span>
                        </div>
                      </Button>
                    ))}
                  </div>

                  <AppFormFieldInfo field={field}>
                    {t("fields:travelExpenses.type.helperText")}
                  </AppFormFieldInfo>
                </div>
              )}
            />
            <formTravelExpenses.Field
              name="files"
              validators={{
                onSubmit: z
                  .array(z.custom<File>())
                  .length(1, t("fields:travelExpenses.files.error.required")),
              }}
              children={(field) => (
                <div className="flex flex-col gap-2">
                  <AppFormFieldLabel field={field} name="travelExpenses.files" />

                  <div className="flex items-center gap-2">
                    <Button
                      color="primary"
                      fill="outline"
                      className="w-full flex-1 ion-rounded-lg"
                      onClick={() => {
                        const fileInput = document.createElement("input");
                        fileInput.type = "file";
                        fileInput.accept = "image/*,application/pdf";
                        fileInput.onchange = (event) => {
                          const target = event.target as HTMLInputElement;
                          const files = Array.from(target.files || []);
                          field.handleChange(files.slice(0, 1));
                        };
                        fileInput.click();
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {field.state.value.length > 0
                          ? field.state.value[0].name
                          : t("fields:travelExpenses.files.placeholder")}
                        <IonIcon icon={fileIcon} className="size-4" />
                      </div>
                    </Button>

                    {field.state.value.length > 0 && (
                      <Button
                        color="danger"
                        fill="clear"
                        className="shrink-0"
                        onClick={() => field.handleChange([])}
                      >
                        <IonIcon icon={xMarkIcon} className="size-4" />
                      </Button>
                    )}
                  </div>

                  {!field.state.meta.errors.length && (
                    <AppFormFieldInfo field={field}>
                      {t("fields:travelExpenses.files.helperText")}
                    </AppFormFieldInfo>
                  )}
                </div>
              )}
            />
            <formTravelExpenses.AppField
              name="cost"
              validators={{
                onChange: z.coerce.number().min(0).step(0.01),
                onSubmit: z.coerce.number().min(0).step(0.01),
              }}
              children={(field) => (
                <field.InputField
                  value={
                    typeof field.state.value === "number"
                      ? field.state.value.toFixed(2)
                      : field.state.value
                  }
                  onIonChange={(event) => field.handleChange(Number(event.detail.value))}
                  type="number"
                  min="0"
                  step="0.01"
                  name="travelExpenses.cost"
                  withPlaceholder
                  withHelperText
                  children={
                    <span
                      slot="start"
                      aria-hidden="true"
                      className="pointer-events-none my-auto !-me-1 !ms-3 select-none text-lg font-medium"
                    >
                      €
                    </span>
                  }
                />
              )}
            />
            <formTravelExpenses.AppField
              name="note"
              validators={{ onChange: z.string(), onSubmit: z.string() }}
              children={(field) => <field.InputField name="travelExpenses.note" withHelperText />}
            />
          </div>
          <IonFooter>
            <IonToolbar className="ion-pb-4 ion-pt-3">
              <formTravelExpenses.SubmitButton />
            </IonToolbar>
          </IonFooter>
        </FormElement>
      </ModalSheetAutoHeight>
    </>
  );
}
